/**
 * Suggestion de fournisseur proche à la saisie (réception), SANS fusion automatique :
 * l'opérateur confirme. Miroir léger de `canonicalSupplierName` côté backend — même clé
 * de normalisation (casse/accents/article/forme juridique) + distance d'édition pour
 * rattraper les fautes de frappe (« Interbop » → « Interbio », « Lomme primeur » →
 * « Lomme Primeurs »). Seuil prudent pour ne pas rapprocher deux fournisseurs distincts.
 */

const LEGAL_FORMS = /\b(sprl|srl|sarl|sàrl|scrl|sca|scs|sa|nv|bvba|bv|asbl|comm v)\b/g;
const LEADING_ARTICLE = /^(les|la|le|l['’]|aux|au)\s+/;

export function normalizeSupplierKey(s: string): string {
  let x = s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
  x = x.replace(/\./g, '').replace(/[/]/g, ' ').replace(/\s+/g, ' ').trim();
  x = x.replace(LEGAL_FORMS, ' ').replace(LEADING_ARTICLE, '');
  return x.replace(/\s+/g, ' ').trim();
}

function levenshtein(a: string, b: string): number {
  const m = a.length;
  const n = b.length;
  if (!m) return n;
  if (!n) return m;
  const dp = Array.from({ length: n + 1 }, (_, i) => i);
  for (let i = 1; i <= m; i++) {
    let prev = dp[0];
    dp[0] = i;
    for (let j = 1; j <= n; j++) {
      const tmp = dp[j];
      dp[j] = a[i - 1] === b[j - 1] ? prev : 1 + Math.min(prev, dp[j], dp[j - 1]);
      prev = tmp;
    }
  }
  return dp[n];
}

/**
 * @returns suggestion : fournisseur connu le plus proche (ou null) ;
 *          exact : true si c'est une simple variante d'orthographe déjà gérée au backend
 *          (dans ce cas on n'affiche PAS de suggestion).
 */
export function suggestSupplier(
  input: string,
  known: string[],
): { suggestion: string | null; exact: boolean } {
  const raw = (input || '').trim();
  if (!raw) return { suggestion: null, exact: false };
  const key = normalizeSupplierKey(raw);
  if (!key) return { suggestion: null, exact: false };

  // Variante d'orthographe / alias curé (même clé) → déjà normalisé au backend, pas de chip.
  for (const k of known) {
    if (normalizeSupplierKey(k) === key) return { suggestion: k, exact: true };
  }

  // Faute de frappe uniquement : plus proche voisin par distance d'édition (seuil prudent).
  // On ne gère QUE les vraies coquilles d'un fournisseur (« Cordir » → « Cordier »). Les
  // alias/formes longues connus (Delibio → Vajra / Delibio, INTERBIO-GPFL-BIO SPRL →
  // Interbio…) sont normalisés de façon déterministe au backend (table SUPPLIER_ALIASES),
  // donc pas de logique par tokens ici (elle confondait canonique et alias).
  let best: { name: string; dist: number } | null = null;
  for (const k of known) {
    const kk = normalizeSupplierKey(k);
    if (!kk || kk === key) continue;
    const d = levenshtein(key, kk);
    if (best === null || d < best.dist) best = { name: k, dist: d };
  }
  if (best && key.length >= 4 && best.dist > 0 && best.dist <= 2) {
    return { suggestion: best.name, exact: false };
  }
  return { suggestion: null, exact: false };
}
